'use client';

import { AnalyticsSkuViewed } from '@ria-client/segment/Analytics/AnalyticsSkuViewed';
import { FeatureViewed } from '@ria-client/segment/components/FeatureViewed';
import type { FinancialAdvisorsProductCardEntityType } from '@ria-client/supergraph/structuredContent/hydration/FinancialAdvisorsProductCard/types';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type React from 'react';
import { useState } from 'react';
import { CardDetails } from './financial-advisors-product-card/card-details';
import { CardDrivers } from './financial-advisors-product-card/card-drivers';
import { CardRibbon } from './financial-advisors-product-card/card-ribbon';
import { CardTabs } from './financial-advisors-product-card/card-tabs';
import { LenderDetails } from './financial-advisors-product-card/lender-details';
import { NW_RATING_DEFAULT_TOOLTIP, resolveDrivers } from './lib/financial-advisors/drivers';
import type { DriverConfig } from './lib/financial-advisors/types';

export interface FinancialAdvisorsProductCardProps {
  marketplaceEntity: FinancialAdvisorsProductCardEntityType;
  showBestForLabel?: boolean;
  bestForLabelOverride?: string;
  firstDriver?: DriverConfig;
  secondDriver?: DriverConfig;
  thirdDriver?: DriverConfig;
  starRatingTooltip?: string;
  monetizingCtaLabel?: string;
  displayAdLabel?: boolean;
}

export function FinancialAdvisorsProductCard({
  props,
}: {
  props: FinancialAdvisorsProductCardProps;
}): React.JSX.Element {
  const {
    marketplaceEntity,
    showBestForLabel,
    bestForLabelOverride,
    firstDriver,
    secondDriver,
    thirdDriver,
    starRatingTooltip,
    monetizingCtaLabel,
    displayAdLabel,
  } = props;
  const [seeDetailsOpened, setSeeDetailsOpened] = useState(false);
  const drivers = resolveDrivers(firstDriver, secondDriver, thirdDriver);
  const hasProsCons = Boolean(
    marketplaceEntity.prosBullets.length && marketplaceEntity.consBullets.length,
  );
  const displaySeeDetails = Boolean(marketplaceEntity.tagline || hasProsCons);
  const ratingTooltip =
    starRatingTooltip ?? marketplaceEntity.starRatingTooltip ?? NW_RATING_DEFAULT_TOOLTIP;

  return (
    <FeatureViewed entityName="financial_advisors_product_card" entitySection="structured_content">
      <AnalyticsSkuViewed
        impressionPosition={0}
        impressionSection="financial_advisors_prod_card"
        institutionId={marketplaceEntity.institution.id}
        institutionName={marketplaceEntity.institution.name}
        level="offer"
        monetizable={marketplaceEntity.monetizable}
        name={marketplaceEntity.name}
        offerId={marketplaceEntity.id}
        offerName={marketplaceEntity.name}
        productId={marketplaceEntity.product.id}
        productName={marketplaceEntity.product.name}
        productType={marketplaceEntity.productType}
        source="marketplace"
      >
        <section className="my-4 overflow-hidden rounded-sm border border-border shadow sm:max-w-[760px]">
          {showBestForLabel && bestForLabelOverride ? (
            <CardRibbon label={bestForLabelOverride} />
          ) : null}
          <div className="px-4 pt-2 pb-2 sm:px-6 sm:pt-6 sm:pb-4">
            <div className="flex flex-row gap-5">
              <div className="flex w-full flex-col justify-between sm:w-[250px] sm:shrink-0">
                <LenderDetails
                  displayAdLabel={displayAdLabel}
                  marketplaceEntity={marketplaceEntity}
                  monetizingCtaLabel={monetizingCtaLabel}
                  starRatingTooltip={ratingTooltip}
                />
                {displaySeeDetails ? (
                  <button
                    className="typography-text-small mt-4 hidden items-center justify-center gap-1 bg-transparent text-secondary-foreground sm:inline-flex"
                    onClick={() => setSeeDetailsOpened(opened => !opened)}
                    type="button"
                  >
                    {seeDetailsOpened ? 'Close details' : 'View details'}
                    {seeDetailsOpened ? (
                      <ChevronUp aria-hidden className="size-4" />
                    ) : (
                      <ChevronDown aria-hidden className="size-4" />
                    )}
                  </button>
                ) : null}
              </div>
              <div className="hidden grow sm:block">
                <CardDrivers
                  drivers={drivers}
                  includeSeparator
                  marketplaceEntity={marketplaceEntity as unknown as Record<string, unknown>}
                />
              </div>
            </div>
          </div>
          {seeDetailsOpened ? (
            <CardDetails
              className="hidden bg-muted p-6 sm:block"
              consBullets={marketplaceEntity.consBullets}
              prosBullets={marketplaceEntity.prosBullets}
              tagline={marketplaceEntity.tagline}
            />
          ) : null}
          <div className="px-4 pb-4 sm:hidden">
            <CardTabs
              drivers={drivers}
              marketplaceEntity={marketplaceEntity as unknown as Record<string, unknown>}
              showMoreDetails={displaySeeDetails}
            />
          </div>
        </section>
      </AnalyticsSkuViewed>
    </FeatureViewed>
  );
}
